import { prefersReducedMotion } from "@/lib/a11y";

class ProductItemGallery extends HTMLElement {
  static observedAttributes = ["data-selected-variant-media-id"];

  constructor() {
    super();
    this.selectors = {
      paginationBullet: "[js-swiper-pagination-bullet]",
      productItemSwatchesList: "[js-product-item-swatches-list]",
      swiperContainer: "[js-swiper]",
      swiperPagination: "[js-swiper-pagination]",
      swiperSlide: "[js-swiper-slide]",
      swiperNextEl: "[js-swiper-next-button]",
      swiperPrevEl: "[js-swiper-prev-button]",
      videoElement: "[js-video]",
    };

    this.attrs = {
      activeURL: "data-active-url",
      index: "data-index",
      featuredMediaId: "data-featured-media-id",
      isPlaying: "data-is-playing",
      isMuted: "data-is-muted",
      mediaId: "data-media-id",
      mediaType: "data-media-type",
    };

    this.classes = {
      videoBullet: "product-item-gallery-pagination__bullet--video",
      videoGlyph: "product-item-gallery-pagination-bullet__glyph",
      videoPill: "product-item-gallery-pagination-bullet__pill",
      paginationWindowed: "product-item__gallery-pagination--windowed",
    };

    this.cssVars = {
      bulletShift: "--gallery-bullet-shift",
    };
  }

  connectedCallback() {
    this.eventController = new AbortController();
    this.eventOptions = { signal: this.eventController.signal };

    this.productItem = this.closest("[data-product-handle]");
    this.productItemSwatchesList = this.productItem.querySelector(this.selectors.productItemSwatchesList);

    this.index = parseInt(this.getAttribute(this.attrs.index), 10);
    this.featuredMediaId = this.getAttribute(this.attrs.featuredMediaId);
    this.swiperContainer = this;
    this.swiperSlides = this.swiperContainer.querySelectorAll(
      this.selectors.swiperSlide
    );
    this.swiperSlidesArray = Array.from(this.swiperSlides);
    this.swiperPagination = Array.from(
      this.closest(".product-item").querySelectorAll(
        this.selectors.swiperPagination
      )
    );
    this.videoElements = Array.from(
      this.querySelectorAll(this.selectors.videoElement)
    );

    this.hintParameters = {
      cycles: 2,
      peekFraction: 1 / 3,
      duration: 320,
      hold: 220,
      restBetween: 900,
    };
    this.hintTimers = [];
    this.hintDone = false;

    this._initCarousel();
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    switch (name) {
      case "data-selected-variant-media-id":
        if (!newValue) break;
        this._cancelHint();
        this._slideToMediaId(newValue);
        break;
    }
  }

  _initCarousel() {
    import(flu.chunks.swiper).then(({ Swiper, Keyboard, Pagination, Navigation }) => {
      this.carousel = new Swiper(this.swiperContainer, {
        modules: [Pagination, Navigation, Keyboard],
        keyboard: {
          enabled: false,
        },
        watchOverflow: true,
        watchSlidesProgress: true,
        grabCursor: true,
        focusableElements: "nothing", // prevent this feature from taking effect
        slidesPerView: "auto", // 'auto' prevents the slide width from being rounded
        navigation: {
          nextEl: this.querySelector(this.selectors.swiperNextEl),
          prevEl: this.querySelector(this.selectors.swiperPrevEl),
        },
        pagination: {
          el: this.swiperPagination,
          type: "bullets",
          dynamicBullets: false,
          clickable: true,
          renderBullet: (index, className) =>
            this._renderBullet(index, className),
        },
        on: {
          afterInit: swiper => this._handleAfterSwiperInitialized(swiper),
          breakpoint: swiper => this._handleSwiperBreakpointChanged(swiper),
          slideChange: swiper => this._handleSlideChanged(swiper),
          touchStart: () => this._cancelHint(),
          navigationNext: () => this._cancelHint(),
          navigationPrev: () => this._cancelHint(),
        },
      });
    });
  }

  _renderBullet(index, className) {
    const slide = this.swiperSlides[index];
    const isVideo =
      slide && slide.getAttribute(this.attrs.mediaType) === "video";

    if (!isVideo) return `<span class="${className}" js-swiper-pagination-bullet></span>`;

    return `<span class="${className} ${this.classes.videoBullet}" js-swiper-pagination-bullet>
              <span class="${this.classes.videoGlyph}"></span>
              <span class="${this.classes.videoPill}"></span>
            </span>`;
  }

  _loadSectionIO() {
    const observer = new IntersectionObserver(
      this._handleSectionIntersect.bind(this),
      { threshold: [0.3, 0.5] }
    );

    observer.observe(this);
    return observer;
  }

  _handleSectionIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
        // this._playActiveSlideVideo(this.swiperInstance);
        this._maybeHint(this.swiperInstance);
      } else {
        // always pause all videos when scrolled out of view.
        this._pauseAllVideos();
      }
    });
  }

  _handleAfterSwiperInitialized(swiper) {
    this.swiperInstance = swiper;
    this.slidesCount = this.swiperInstance.slides.length;

    if (this.slidesCount == 0) return;

    // Manually control keyboard actions
    this.swiperContainer.addEventListener(
      "focus",
      () => {
        this.swiperInstance.keyboard.enable();
      },
      this.eventOptions
    );

    this.swiperContainer.addEventListener(
      "blur",
      () => {
        this.swiperInstance.keyboard.disable();
      },
      this.eventOptions
    );

    this.swiperContainer.addEventListener(
      "keydown",
      e => {
        if (e.key === "Enter") {
          this.swiperInstance.slides[this.swiperInstance.activeIndex].click();
        }
      },
      this.eventOptions
    );

    this.sectionIO = this._loadSectionIO();
    this._updateBulletWindow(0);

    // Switch to the filtered or searched featured media when necessary. This is done without a transition.
    const firstSlideMediaId = this.swiperInstance.slides[0].getAttribute(
      this.attrs.mediaId
    );

    if (firstSlideMediaId !== this.featuredMediaId) {
      this._slideToMediaId(this.featuredMediaId, 0);
    }
  }

  _handleSlideChanged(swiper) {
    this._playActiveSlideVideo(swiper);
    this._updateBulletWindow(swiper.activeIndex);
  }

  _handleSwiperBreakpointChanged(swiper) {
    this._playActiveSlideVideo(swiper);
  }

  _maybeHint(swiper) {
    if (this.index !== 1 || this.hintDone || prefersReducedMotion()) return;
    if (this.slidesCount < 2) return;

    this.hintDone = true;

    const startIndex = swiper.activeIndex;
    const resting = swiper.translate;
    if (typeof resting !== "number" || isNaN(resting)) return;

    const nudge = (to, ms) => {
      swiper.setTransition(ms);
      swiper.setTranslate(to);
    };

    const cycle = remaining => {
      if (remaining <= 0) {
        swiper.setTransition(0);
        swiper.slideTo(startIndex, 0, false);
        return;
      }

      const slideWidth =
        (swiper.slidesSizesGrid && swiper.slidesSizesGrid[startIndex]) ||
        swiper.width;
      const distance = Math.round(
        slideWidth * this.hintParameters.peekFraction
      );
      if (!distance) return;

      nudge(resting - distance, this.hintParameters.duration);

      this.hintTimers.push(
        setTimeout(() => {
          nudge(resting, this.hintParameters.duration);

          this.hintTimers.push(
            setTimeout(
              () => cycle(remaining - 1),
              this.hintParameters.restBetween
            )
          );
        }, this.hintParameters.duration + this.hintParameters.hold)
      );
    };

    this.hintTimers.push(
      setTimeout(
        () => cycle(this.hintParameters.cycles),
        this.hintParameters.restBetween
      )
    );
  }

  _cancelHint() {
    this.hintTimers.forEach(clearTimeout);
    this.hintTimers = [];
    this.hintDone = true;
  }

  _slideToMediaId(mediaId, speed) {
    const selectedSlideIndex = this.swiperSlidesArray.findIndex(slide => {
      return slide.getAttribute(this.attrs.mediaId) === mediaId;
    });

    this.swiperInstance.slideTo(selectedSlideIndex, speed);
    const activeSlide =
      this.swiperInstance.slides[this.swiperInstance.activeIndex];
    const activeSlideURL = activeSlide.href;

    if (this.productItemSwatchesList) {
      this.productItemSwatchesList.setAttribute(this.attrs.activeURL, activeSlideURL);
    }
  }

  _playActiveSlideVideo(swiper) {
    if (!swiper || swiper.slides.length < 1) return;
    if (this.videoElements.length < 1) return;

    const activeSlideElement = swiper.slides[swiper.activeIndex];
    const activeSlideVideoElement = activeSlideElement.querySelector(
      this.selectors.videoElement
    );
    const activeSlideVideoIndex = this._getVideoIndex(activeSlideVideoElement);

    this._toggleVideos(activeSlideVideoIndex);
  }

  _getVideoIndex(element) {
    return this.videoElements.indexOf(element);
  }

  _toggleVideos(interactedIndex) {
    this.videoElements.forEach((video, index) => {
      index == interactedIndex
        ? this._playVideo(index)
        : this._pauseVideo(index);
    });
  }

  _playVideo(index) {
    this.videoElements[index].play();
  }

  _pauseVideo(index) {
    this.videoElements[index].pause();
  }

  _pauseAllVideos() {
    this.videoElements.forEach((video, index) => {
      this._pauseVideo(index);
    });
  }

  _updateBulletWindow(activeIndex) {
    if (this.swiperPagination.length < 1) return;

    const maxVisibleBullets = 5;
    if (this.slidesCount <= maxVisibleBullets) return;

    this.swiperPagination.forEach(pagination => {
      pagination.classList.add(this.classes.paginationWindowed);

      const dotSize = 5;
      const gap = 6;

      const lead = Math.floor((maxVisibleBullets - 1) / 2);
      const firstVisible = Math.min(
        Math.max(activeIndex - lead, 0),
        this.slidesCount - maxVisibleBullets
      );

      const offset = firstVisible * (dotSize + gap) * -1;

      pagination.style.setProperty(this.cssVars.bulletShift, `${offset}px`);
    });
  }

  disconnectedCallback() {
    this.eventController.abort();
    this.swiperInstance?.destroy();
    this.sectionIO?.disconnect();
    this._cancelHint();
  }
}

if (!customElements.get("product-item-gallery")) {
  customElements.define("product-item-gallery", ProductItemGallery);
}
